import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "../../toast/toast";
import { Row } from "@tanstack/react-table";
import TablePaginationControls from "../../components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "../../components/TableandMobilecomponent";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "../../components/DatatablesActionIcons";
import DeleteConfirmModal from "../../components/ToolTips/DeleteConfirmModal";
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
    createColumnHelper,
} from "@tanstack/react-table";

// ---------- Types ----------
type InvoiceTemplateRow = {
    _id: string;
    template_name: string;
    company_name?: string;
    default_template?: boolean;
    created_at?: string;
};

// ---------- Helpers ----------
function fuzzyTextFilter<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string
) {
    const raw = row.getValue(columnId);
    const value = (typeof raw === "boolean" ? (raw ? "Yes" : "No") : String(raw)).toLowerCase();
    return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<InvoiceTemplateRow>();

// ============================================================
// Component
// ============================================================
const SignageInvoiceTemplates: React.FC = () => {
    const [rows, setRows] = useState<InvoiceTemplateRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [rowCount, setRowCount] = useState(0);
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

    const token = typeof window !== "undefined" ? getAuthCookie("sessionToken") : null;
    const location = useLocation();
    const isSignageTeammember = location.pathname.includes("/signageteammember");
    const baseRoute = isSignageTeammember ? "/signageteammember" : "/signage";
    const { canCreate, canEdit, canDelete } = useModuleActionPermissions(
        MODULE_PERMISSION_NAMES.invoice_templates
    );

    const [globalFilter, setGlobalFilter] = useState("");
    const [searchInput, setSearchInput] = useState<string>("");
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

    const topRef = useRef<HTMLDivElement>(null);

    // ---------- Fetch datatable ----------
    useEffect(() => {
        let ignore = false;

        async function load() {
            setLoading(true);
            try {
                const body = {
                    page: pagination.pageIndex + 1,
                    limit: pagination.pageSize,
                    search: globalFilter,
                    filters: {}, // extend if needed later
                    sort: { field: "template_name", order: "asc" },
                    // Optionally request only fields your UI needs:
                    // fields: ["_id","template_name","default","created_at"]
                };

                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/invoice-templates/datatable`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(body),
                    }
                );

                const json: any = await res.json();
                if (!res.ok || json?.error) {
                    const msg = json?.error || json?.message || "Failed to fetch templates";
                    showError(msg);
                    throw new Error(msg);
                }

                if (!ignore) {
                    setRows(Array.isArray(json?.data) ? json.data : []);
                    setRowCount(Number(json?.total) || 0);
                }
            } catch (e) {
                if (!ignore) {
                    console.error(e);
                    setRows([]);
                    setRowCount(0);
                }
            } finally {
                if (!ignore) setLoading(false);
            }
        }

        load();
        return () => {
            ignore = true;
        };
    }, [token, globalFilter, pagination, deleting]);

    const handleDeleteClick = (template: InvoiceTemplateRow) => {
        setDeleteTarget({ id: template._id, name: template.template_name || "Invoice Template" });
    };

    const handleDeleteCancel = () => {
        setDeleteTarget(null);
        setDeleting(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        await handleDelete(deleteTarget.id);
        setDeleteTarget(null);
    };

    // ---------- Delete ----------
    const handleDelete = async (id: string) => {
        setDeleting(id);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/invoice-templates/${id}`,
                {
                    method: "DELETE",
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            const json: any = await res.json();
            if (!res.ok || json?.error) {
                const msg = json?.error || json?.message || "Failed to delete template";
                showError(msg);
                throw new Error(msg);
            }
            showSuccess("Template deleted successfully!");
            // Optimistic local update
            setRows((prev) => prev.filter((r) => r._id !== id));
            setRowCount((n) => Math.max(0, n - 1));
        } catch (e) {
            console.error(e);
        } finally {
            setDeleting(null);
        }
    };

    // ---------- Columns ----------
    const columns = useMemo(
        () => [
            columnHelper.display({
                id: "serial",
                header: () => <div className="th-text S-No">S.No.</div>,
                cell: (info) => (
                    <div className="text-sm S-No">
                        {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
                    </div>
                ),
            }),

            columnHelper.accessor((row) => row.company_name, {
                id: "company_name",
                header: () => <div className="th-text S-No-2">Template Used For</div>,
                cell: (info) => {
                    const r = info.row.original;
                    return (
                        <div className="text-sm S-No-2">
                            <Link to={`${baseRoute}/InvoiceTemplate/${r._id}`} className="link-primary text-decoration-none">
                                {info.getValue() || "--"}
                            </Link>
                        </div>
                    );
                },
                filterFn: fuzzyTextFilter,
                enableGlobalFilter: true,
            }),

            columnHelper.accessor((row: any) => row.default_template, {
                id: "default",
                header: () => <div className="th-text">Default Template</div>,
                cell: (info) => {
                    const raw = info.getValue() as unknown;
                    const isYes = raw === true || String(raw).toLowerCase() === "true";

                    return (
                        <div className="text-sm">
                            <span
                                className={`badge rounded-pill px-3 py-2 fs-15 ${isYes
                                    ? "bg-success-subtle text-success-emphasis"
                                    : "bg-body-tertiary text-secondary-emphasis"
                                    }`}
                            >
                                {isYes ? "Yes" : "No"}
                            </span>
                        </div>
                    );
                },
            }),


            columnHelper.display({
                id: "actions",
                header: () => <div className="th-text">Actions</div>,
                cell: (info) => {
                    const r = info.row.original;
                    if (!canEdit && !canDelete) return null;
                    return (
                        <div className="d-flex align-items-center gap-2">
                            {/* Optional view – points to same edit page in read mode if you have one */}
                            {/* <ViewIconButton to={`${baseRoute}/InvoiceTemplate/${r._id}?view=1`} title="View" /> */}
                            {canEdit && (
                                <EditIconButton to={`${baseRoute}/InvoiceTemplate/${r._id}`} title="Edit" />
                            )}
                            {canDelete && (
                                <DeleteIconButton
                                    title="Delete"
                                    disabled={deleting === r._id}
                                    onClick={() => handleDeleteClick(r)}
                                />
                            )}
                        </div>
                    );
                },
            }),
        ],
        [deleting, pagination, baseRoute, canEdit, canDelete]
    );

    // ---------- Table ----------
    const table = useReactTable({
        data: rows,
        columns,
        pageCount: Math.ceil(rowCount / pagination.pageSize),
        state: { pagination, globalFilter },
        manualPagination: true,
        manualFiltering: true,
        onPaginationChange: setPagination,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
    });

    useEffect(() => {
        topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [pagination.pageIndex]);

    return (
        <div className="content container" ref={topRef}>
            {/* Top right add button */}
            <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
                <h4 className="mb-0"></h4>
                <div className="d-flex gap-3 align-items-center">
                    {canCreate && (
                        <Link to={`${baseRoute}/InvoiceTemplate`} className="btn btn-primary table-addbutton">
                            + Invoice Template
                        </Link>
                    )}
                </div>
            </div>

            <div className="po-header mb-4 shadow-border-datatables">
                <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                    <div className="po-title-wrap me-2 d-flex align-items-center">
                        <h4 className="po-title mb-0">Orders/Invoice Templates</h4>
                    </div>

                    {/* tools block (search) — form wrapper helps browsers honor autocomplete=off */}
                    <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
                        <form
                            className="input-group po-search"
                            autoComplete="off"
                            onSubmit={(e) => {
                                e.preventDefault();
                                setGlobalFilter(searchInput);
                            }}
                        >
                            <span className="input-group-text">
                                <i className="ri-search-line" />
                            </span>
                            <input
                                type="text"
                                id="signageInvoiceTemplatesSearchInput"
                                name="signage_invoice_templates_datatable_search"
                                className="form-control"
                                placeholder="Search by Template Name"
                                value={searchInput}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                                    if (e.key === "Enter") setGlobalFilter(searchInput);
                                }}
                                autoComplete="off"
                                autoCorrect="off"
                                spellCheck={false}
                            />
                        </form>
                    </div>
                </div>
            </div>

            <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
                <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
                    <h5 className="mb-0"></h5>
                </div>
                <hr className="mb-2" style={{ borderTop: "none" }} />

                {/* Desktop */}
                <div className="rdt-desktop">
                    <div className="production-scroll" style={{ "--rdt-min": "1200px" } as React.CSSProperties}>
                        <table className="table production-table align-middle mb-0 table-1">
                            <DesktopTableParts table={table} />
                        </table>
                    </div>
                </div>

                {/* Mobile */}
                <div className="rdt-mobile">
                    <MobileCardsOnly
                        table={table}
                        idColId="_id"
                        titleColId="template_name"
                        actionColId="actions"
                    />
                </div>
            </div>

             {table.getRowModel().rows.length === 0 ? (
                <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
                    {loading ? "Loading..." : "No records found"}
                </div>
            ) : (
                <div className="mt-4 mb-4">
                    <TablePaginationControls table={table} />
                </div>
            )}
            <DeleteConfirmModal
                open={!!deleteTarget}
                dynamicName={deleteTarget?.name || "Invoice Template"}
                onCancel={handleDeleteCancel}
                onDelete={handleDeleteConfirm}
            />
        </div>
    );
};

export default SignageInvoiceTemplates;
